import React, { useContext, useEffect, useRef, useState } from 'react';

import cx from 'classnames';

import { useFormik } from 'formik';

import * as yup from 'yup';

import { AuthContext } from 'Auth/AuthContext';

import { canMoveBilling } from 'Customers/BillingCapacity/billingPermissions';

import PurchasedSeatsReview, {
  hasPurchaseContext,
  purchaseLabels,
} from 'Customers/BillingCapacity/PurchasedSeatsReview';

import TextInput from 'components/forms/TextInput/TextInput';

import RoundedButton from 'components/RoundedButton/RoundedButton';

import SelectField from 'components/SelectField';

import SpinnerRing from 'components/SpinnerRing';

import Toast from 'components/Toast/Toast';

import { PACKAGE_NAMES } from 'Customers/constants';

const EditSubscriptionForm = ({
  onClose,
  onSubmit,
  initialValues,
  selected,
  users,
  sent,
  isSending,
  billingPreview,
  onReviewChange,
}) => {
  const { authState } = useContext(AuthContext) || {};
  const language = authState?.user?.language === 'ar' ? 'ar' : 'en';
  const copy = purchaseLabels[language];
  const [reviewing, setReviewing] = useState(false);
  const [phase, setPhase] = useState('review');
  const sending = useRef(false);
  const requestId = useRef(null);
  const quantityInput = useRef(null);
  const referral = !!billingPreview;
  const purchaseReady = hasPurchaseContext(billingPreview, selected) && canMoveBilling(authState?.user);
  useEffect(() => {
    if (referral && !reviewing) quantityInput.current?.focus();
  }, [referral, reviewing]);
  const formik = useFormik({
    initialValues: referral
      ? {
          ...initialValues,
          price: billingPreview.rates?.active ?? '',
          numberOfUnits: billingPreview.purchased?.active ?? '',
        }
      : initialValues,
    validationSchema: yup.object({
      price: referral ? yup.mixed() : yup.number().positive('price must be positive').required('price is required'),
      numberOfUnits: referral
        ? yup
            .number()
            .integer(copy.invalid)
            .max(Number.MAX_SAFE_INTEGER, copy.invalid)
            .min(Math.max(1, billingPreview.purchased?.active ?? 0), copy.invalid)
            .required(copy.invalid)
        : selected.pricingModel === 'PER_UNIT'
        ? yup.number().positive('number of units must be positive').required('number of units is required')
        : yup.number().optional().nullable(),
      implementationUser:
        !referral && selected.requiresUser ? yup.string().required('Assign User is required') : yup.string().nullable(),
    }),
    onSubmit: (values, { setSubmitting }) => {
      if (referral) {
        if (purchaseReady) {
          setReviewing(true);
          setPhase('review');
          onReviewChange?.(true);
        }
        setSubmitting(false);
        return;
      }
      const submissionValues = {
        price: values.price,
        numberOfUnits: selected.pricingModel === 'PER_UNIT' ? values?.numberOfUnits || 0 : 1,
        implementationUser: values?.implementationUser || '',
      };
      onSubmit(submissionValues).finally(() => setSubmitting(false));
    },
  });

  const confirmPurchase = async () => {
    if (sending.current || !purchaseReady || !['review', 'failed'].includes(phase)) return;
    sending.current = true;
    if (!requestId.current) requestId.current = window.crypto.randomUUID();
    setPhase('sending');
    try {
      await onSubmit({
        quantityOnly: true,
        previewId: billingPreview.previewId,
        requestId: requestId.current,
        numberOfUnits: Number(formik.values.numberOfUnits),
      });
      setPhase('success');
    } catch (error) {
      const code = error.response?.data?.code;
      setPhase(
        code === 'BILLING_PREVIEW_STALE'
          ? 'stale'
          : ['BILLING_REQUEST_MISMATCH', 'BILLING_REQUEST_CONFLICT'].includes(code)
          ? 'conflict'
          : ['BILLING_PENDING', 'BILLING_LOCAL_FINALIZATION_PENDING', 'BILLING_PROVIDER_RESULT_MISMATCH'].includes(code)
          ? 'pending'
          : ['BILLING_QUANTITY_ONLY_INVALID', 'BILLING_QUANTITY_ONLY_DECREASE', 'BILLING_OPERATION_REJECTED'].includes(
              code
            ) || [401, 403].includes(error.response?.status)
          ? 'rejected'
          : 'failed'
      );
    } finally {
      sending.current = false;
    }
  };
  if (referral && !purchaseReady)
    return (
      <div className='p-4' dir={language === 'ar' ? 'rtl' : 'ltr'}>
        <p role='alert'>{copy.unavailable}</p>
        <RoundedButton text={copy.refresh} onClick={onClose} color='primary' />
      </div>
    );
  if (referral && reviewing)
    return (
      <PurchasedSeatsReview
        preview={billingPreview}
        plan={PACKAGE_NAMES[selected.plan] || selected.plan}
        quantity={Number(formik.values.numberOfUnits)}
        language={language}
        phase={phase}
        onRefresh={onClose}
        onConfirm={confirmPurchase}
        onBack={() => {
          setReviewing(false);
          onReviewChange?.(false);
        }}
      />
    );
  if (referral)
    return (
      <form noValidate onSubmit={formik.handleSubmit} dir={language === 'ar' ? 'rtl' : 'ltr'} className='p-4'>
        <p>{billingPreview.target.tenantName}</p>
        <label className='d-block mb-3' htmlFor='purchase-seats'>
          {copy.units}
          <input
            ref={quantityInput}
            id='purchase-seats'
            className='form-control'
            type='number'
            name='numberOfUnits'
            value={formik.values.numberOfUnits}
            min={billingPreview.purchased.active}
            step='1'
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            required
          />
        </label>
        {formik.touched.numberOfUnits && formik.errors.numberOfUnits && <p role='alert'>{copy.invalid}</p>}
        <label className='d-block mb-3' htmlFor='purchase-rate'>
          {copy.unitPrice}
          <input
            id='purchase-rate'
            className='form-control'
            type='number'
            value={billingPreview.rates.active}
            readOnly
          />
        </label>
        <p>
          <bdi dir='ltr'>{billingPreview.rates.currency}</bdi> / {billingPreview.rates.period.count}{' '}
          {copy[billingPreview.rates.period.unit]}
        </p>
        <div className='d-flex flex-wrap justify-content-end gap-2 border-top pt-3'>
          <RoundedButton text={copy.cancel} color='white' onClick={onClose} />
          <RoundedButton text={copy.review} type='submit' color='primary' />
        </div>
      </form>
    );

  const renderPlaceholder = () => (
    <div className='d-flex align-items-center gap-1'>
      <span>
        <i className='icon-user-circle me-2' />
        Select
      </span>
    </div>
  );
  return (
    <>
      {isSending && <SpinnerRing />}
      {sent && <Toast show type='success' message='Subscription updated successfully' duration={2000} />}
      <form noValidate onSubmit={formik.handleSubmit}>
        <div className='row mx-3'>
          <div className='col-md-12 my-3'>
            <TextInput
              name='plan'
              label='package'
              className='font-size-16'
              value={PACKAGE_NAMES[selected.plan] || selected.plan}
              readOnly
            />
          </div>
          {selected.pricingModel === 'PER_UNIT' && (
            <div className='col-md-12 mb-3'>
              <TextInput
                name='numberOfUnits'
                label='number of units'
                type='number'
                value={formik.values.numberOfUnits}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isRequired
                error={formik.touched.numberOfUnits && formik.errors.numberOfUnits}
              />
            </div>
          )}
          <div className='col-md-6 mb-3'>
            <TextInput
              name='price'
              label={selected.pricingModel === 'PER_UNIT' ? 'Unit price' : 'Flat fee'}
              type='number'
              value={formik.values.price}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isRequired
              error={formik.touched.price && formik.errors.price}
            />
          </div>
          <div className='col-md-6 mb-3'>
            <SelectField
              isCustom
              label='Currency'
              options={[
                {
                  id: selected.currency,
                  name: selected.currency,
                  option: selected.currency,
                  value: selected.currency,
                },
              ]}
              value={selected.currency}
              isDisabled
              isRequired
            />
          </div>
          {selected.requiresUser && (
            <div className='col-md-12 mb-3'>
              <SelectField
                isCustom
                name='implementationUser'
                placeholder={renderPlaceholder()}
                label='Assign User'
                options={users}
                value={formik.values.implementationUser}
                onChange={formik.handleChange}
                error={formik.touched.implementationUser && formik.errors.implementationUser}
                isRequired
              />
            </div>
          )}
        </div>
        <div className={cx('d-flex', 'justify-content-end gap-2', 'py-2 my-2', 'pe-4', 'border-t')}>
          <RoundedButton text='cancel' btnStyle='contained' color='white' size='md' onClick={onClose} />
          <RoundedButton loading={isSending} type='submit' text='Save' btnStyle='contained' color='primary' size='md' />
        </div>
      </form>
    </>
  );
};

export default EditSubscriptionForm;
